import React, {
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { AuthContext } from "../../../context/AuthContext";
import AppointmentTable from "./AppointmentTable";
import API from "../../../services/interceptor";
import Loading from "../../Loading";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Calendar, Clock, FileText, User as UserIcon } from "lucide-react";

// Simplified animations without external dependencies
const AnimatedContainer = ({ children, className }) => (
  <div className={`transition-all duration-300 ease-in-out ${className}`}>
    {children}
  </div>
);

const MyAppointment = () => {
  const {
    loginUser,
    getUserDetails,
    userDetails,
    userDetailsLoading,
    userDetailsError,

    setUserDetailsError,
  } = useContext(AuthContext);

  const [appointmentDate, setAppointmentDate] = useState("");
  const [timeBlock, setTimeBlock] = useState("");
  const [reason, setReason] = useState("");
  const [otherReason, setOtherReason] = useState("");
  const [loading, setLoading] = useState(false);

  const timeBlocks = [
    "10:00 AM - 12:00 PM",
    "12:00 PM - 2:00 PM",
    "2:00 PM - 4:00 PM",
    "4:00 PM - 6:00 PM",
    "6:00 PM - 8:00 PM",
  ];

  const reasonOptions = [
    "Routine Check-Up",
    "Cleaning",
    "Fillings",
    "Extraction",
    "Orthodontics",
    "Root Canal Treatment",
    "Teeth Whitening",
    "Dental Implant",
    "Braces Consultation",
    "Emergency",
    "Other",
  ];

  const today = new Date();
  const maxDate = new Date(today);
  maxDate.setDate(today.getDate() + 14);

  const resetForm = useCallback(() => {
    setAppointmentDate("");
    setTimeBlock("");
    setReason("");
    setOtherReason("");
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setLoading(true);
      setUserDetailsError("");

      // Validation
      if (!appointmentDate || !timeBlock || !reason) {
        toast.error("Please fill in all required fields.", {
          position: "top-center",
          autoClose: 3000,
        });
        setLoading(false);
        return;
      }

      if (reason === "Other" && !otherReason.trim()) {
        toast.error("Please specify the reason for your visit.", {
          position: "top-center",
          autoClose: 3000,
        });
        setLoading(false);
        return;
      }

      toast.loading("Submitting Your Appointment...", {
        position: "top-center",
        toastId: "submit-appointment",
      });

      try {
        await API.post(
          `${process.env.REACT_APP_API_URL}/api/users/appointment`,
          {
            userId: loginUser.id,
            date: appointmentDate,
            timeBlock,
            reason: reason === "Other" ? otherReason : reason,
          }
        );

        toast.dismiss("submit-appointment");
        toast.success("Your Appointment Booked Successfully!", {
          position: "top-center",
          autoClose: 3000,
        });

        resetForm();
        getUserDetails(loginUser.id);
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || "Failed to submit your appointment";

        toast.dismiss("submit-appointment");
        toast.error(errorMessage, {
          position: "top-center",
          autoClose: 3000,
        });

        setUserDetailsError(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [
      appointmentDate,
      timeBlock,
      reason,
      otherReason,
      loginUser.id,
      getUserDetails,
      resetForm,
      setUserDetailsError,
    ]
  );

  // Fetch existing medical details on component mount
  useEffect(() => {
    if (
      !userDetails ||
      (Array.isArray(userDetails) && userDetails.length === 0)
    ) {
      getUserDetails(loginUser.id);
    }
  }, [userDetails, getUserDetails, loginUser.id]);

  // Memoized Header Section
  const HeaderSection = useMemo(
    () => (
      <div className="text-center my-10 ">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          My Appointment
        </h1>
      </div>
    ),
    []
  );

  // Memoized Appointment Form Section
  const AppointmentFormSection = useMemo(
    () => (
      <div className="shadow-md rounded-lg bg-gradient-to-r from-white to-white p-6 mb-6">
        <h2 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center gap-2">
          <Calendar size={24} className="text-blue-600" />
          Book New Appointment
        </h2>

        {userDetailsLoading ? (
          <Loading />
        ) : userDetailsError ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <p className="text-red-600 font-medium">{userDetailsError}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Date and Time Selection */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="mb-4">
                <label
                  htmlFor="appointmentDate"
                  className="block font-semibold mb-1"
                >
                  Preferred Appointment Date:{" "}
                  <span style={{ color: "red" }}> * </span>
                </label>
                <div className="relative">
                  <Calendar
                    className="absolute top-3 left-3 text-blue-400"
                    size={16}
                  />
                  <input
                    type="date"
                    id="appointmentDate"
                    value={appointmentDate}
                    onChange={(e) => setAppointmentDate(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min={today.toISOString().split("T")[0]}
                    max={maxDate.toISOString().split("T")[0]}
                    required
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="timeBlock" className="block font-semibold mb-1">
                  Preferred Time Block:{" "}
                  <span style={{ color: "red" }}> * </span>
                </label>
                <div className="relative">
                  <Clock
                    className="absolute top-3 left-3 text-blue-400"
                    size={16}
                  />
                  <select
                    id="timeBlock"
                    value={timeBlock}
                    onChange={(e) => setTimeBlock(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="" disabled>
                      Select a time block
                    </option>
                    {timeBlocks.map((block) => (
                      <option key={block} value={block}>
                        {block}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Reason for Visit */}
            <div className="mb-4">
              <label htmlFor="reason" className="block font-semibold mb-1">
                Reason for Visit: <span style={{ color: "red" }}> * </span>
              </label>
              <div className="relative">
                <FileText
                  className="absolute top-3 left-3 text-blue-400"
                  size={16}
                />
                <select
                  id="reason"
                  value={reason}
                  onChange={(e) => {
                    setReason(e.target.value);
                    if (e.target.value !== "Other") setOtherReason("");
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="" disabled>
                    Select a reason
                  </option>
                  {reasonOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              {reason === "Other" && (
                <div className="mt-2">
                  <input
                    type="text"
                    placeholder="Please specify your reason"
                    value={otherReason}
                    onChange={(e) => setOtherReason(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div className="flex justify-center mt-8">
              <button
                type="submit"
                className={`bg-blue-500 text-white font-bold py-3 px-8 rounded-lg hover:bg-blue-600 shadow-lg hover:shadow-xl transition-all transform hover:scale-105 ${
                  loading ? "opacity-50 cursor-not-allowed" : ""
                }`}
                disabled={loading}
              >
                {loading ? "Booking..." : "Book Appointment"}
              </button>
            </div>
          </form>
        )}
      </div>
    ),
    [
      userDetailsLoading,
      userDetailsError,
      appointmentDate,
      timeBlock,
      reason,
      otherReason,
      loading,
      handleSubmit,
      today,
      maxDate,
      timeBlocks,
      reasonOptions,
    ]
  );

  // Memoized Appointments History Section
  const AppointmentsHistorySection = useMemo(
    () => (
      <div className="shadow-md rounded-lg bg-gradient-to-r from-white to-white p-6">
        <h2 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center gap-2">
          <UserIcon size={24} className="text-green-600" />
          My Appointment History
        </h2>

        {userDetailsLoading ? (
          <Loading />
        ) : userDetails &&
          userDetails.appointmentDetails &&
          userDetails.appointmentDetails.length > 0 ? (
          <AppointmentTable userDetails={userDetails.appointmentDetails} />
        ) : (
          <div className="text-center py-8">
            <div className="bg-gray-50 rounded-lg p-8">
              <Calendar size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-600 mb-2">
                No Appointments Yet
              </h3>
              <p className="text-gray-500">
                You haven't booked any appointments yet. Book your first
                appointment using the form above.
              </p>
            </div>
          </div>
        )}
      </div>
    ),
    [userDetailsLoading, userDetails] // Remove .appointmentDetails from dependency
  );

  return (
    <AnimatedContainer className="mx-auto sm:p-6">
      {HeaderSection}

      {/* Appointment Booking Form */}
      {AppointmentFormSection}

      {/* Appointments History */}
      {AppointmentsHistorySection}

      <ToastContainer />
    </AnimatedContainer>
  );
};

export default MyAppointment;
